import '../src/styles/root/index.scss';
import React, { Suspense, lazy } from 'react';
import App from './App';
// import reportWebVitals from './reportWebVitals';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store, { persistor } from './store/store';
import { PersistGate } from 'redux-persist/integration/react';
import { HistoryRouter } from './components/routers/HistoryRouter';
import history from './history';
import './internationalization/i18n';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { APP_ENV, BUGSNAG_API_KEY } from './lib/constants/central-api';
import { enableDemoMode, isDemoMode } from './lib/demo/demo-mode';
import { installDemoBackend } from './lib/demo/demo-backend';
import { seedDemoStore } from './lib/demo/demo-seed';
// Demo 2.0 — the NEW demo lives at /demo-new, fully forked from the frozen
// /demo (which clients already hold links to). Separate mode, backend, seed.
import { enableNewDemoMode, isNewDemoMode } from './lib/demo-new/demo-mode';
import { installNewDemoBackend } from './lib/demo-new/demo-backend';
import { seedNewDemoStore } from './lib/demo-new/demo-seed';
import {
  captureDemoAttribution,
  captureInviteToken,
} from './lib/demo-new/demo-lead';
import { installPlanLockWatcher } from './lib/plan-lock/plan-lock';

//* Domain migration redirect handler
const currentProtocol = window.location.protocol;
const currentHost = window.location.host;
const currentPath = window.location.pathname;
const currentSearch = window.location.search;
const currentHash = window.location.hash;

//? Domain mapping
const domainMappings: Record<string, string> = {
  'seller.adup.io': 'tara.adup.io',
  'seller-staging.adup.io': 'tara-staging.adup.io',
  'seller-dev.kodeia.com': 'tara-dev.kodeia.com',
};

if (domainMappings[currentHost]) {
  const newDomain = domainMappings[currentHost];
  const newUrl = `${currentProtocol}//${newDomain}${currentPath}${currentSearch}${currentHash}`;

  console.log(`🔄 Domain migration redirect: ${currentHost} → ${newDomain}`);

  //? Perform redirect while preserving everything
  window.location.replace(newUrl);
}

// * disabling console.messages & debuggers in LIVE instance
if (
  !(
    process.env.NODE_ENV === 'development' ||
    window.location.href.split('/')[2].includes('dev') ||
    window.location.href.split('/')[2].includes('staging')
  )
) {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
  console.error = () => {};
}

//? Create a Bugsnag instance
Bugsnag.start({
  apiKey: BUGSNAG_API_KEY,
  plugins: [new BugsnagPluginReact()],
  releaseStage: APP_ENV,
  enabledReleaseStages: ['LIVE', 'STAGING'],
});
BugsnagPerformance.start({
  apiKey: BUGSNAG_API_KEY,
  releaseStage: APP_ENV,
  enabledReleaseStages: ['LIVE', 'STAGING'],
});
// On LIVE/STAGING the Bugsnag React boundary catches render throws. On DEV the
// boundary used to be a passthrough — so ANY render throw (e.g. a bad data row)
// unmounted the whole app and the page went blank with no on-screen clue. Use a
// real boundary on DEV too that shows the error inline instead of white-screening.
class DevErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[DevErrorBoundary] render crash:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '2rem',
            margin: '2rem',
            border: '1px solid #e4e4e7',
            borderRadius: '0.8rem',
            fontFamily: 'monospace',
            color: '#b91c1c',
            background: '#fff',
            whiteSpace: 'pre-wrap',
          }}
        >
          <strong>Something crashed while rendering this page (dev only).</strong>
          {'\n\n'}
          {String(this.state.error?.message ?? this.state.error)}
          {'\n\n'}
          <button
            type='button'
            onClick={() => this.setState({ error: null })}
            style={{ marginTop: '1rem', cursor: 'pointer' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return <>{this.props.children}</>;
  }
}

const ErrorBoundary =
  APP_ENV !== 'DEV'
    ? Bugsnag.getPlugin &&
      (Bugsnag.getPlugin('react')?.createErrorBoundary(React) as any)
    : DevErrorBoundary;

// Demo mode — must run BEFORE React mounts so the fetch interceptor is in place
// for the first API call and the store is seeded for the first render.
// NOTE: '/demo-new' does NOT match isDemoMode() ('/demo-new'.startsWith('/demo/')
// is false), so exactly one of these branches runs per load.
if (isNewDemoMode()) {
  enableNewDemoMode();
  // Grab ?invite= BEFORE the router's redirect chain strips the query.
  captureInviteToken();
  // Same for the campaign values (utm_*, click ids, external referrer).
  captureDemoAttribution();
  installNewDemoBackend();
  seedNewDemoStore();
} else if (isDemoMode()) {
  enableDemoMode();
  installDemoBackend();
  // Seed the fake session before the first render so every /demo/* route is
  // authed (the whole app runs under the /demo basename — see below).
  seedDemoStore();
} else {
  // The real app: a 402 `no_active_plan` from any API call locks it to the
  // subscription page. The demos have no plan and never lock.
  installPlanLockWatcher();
}

// The demos are fully separate URL spaces: when the path is under /demo (or
// /demo-new), mount the entire app with that basename so every route lives at
// /demo/* resp. /demo-new/* and internal navigation never touches the app at /.
const DEMO_BASENAME =
  typeof window === 'undefined'
    ? undefined
    : window.location.pathname === '/demo-new' ||
        window.location.pathname.startsWith('/demo-new/')
      ? '/demo-new'
      : window.location.pathname === '/demo' ||
          window.location.pathname.startsWith('/demo/')
        ? '/demo'
        : undefined;

// Demo 2.0 — the app (routes, pages, demo data) is only mounted once the
// visitor has passed the demo gate. Lazy: stays out of the main bundle.
const DemoNewGateBoundary = lazy(
  () => import('./pages/demo-new/DemoGateBoundary')
);
const appRoot = isNewDemoMode() ? (
  <Suspense fallback={null}>
    <DemoNewGateBoundary>
      <App />
    </DemoNewGateBoundary>
  </Suspense>
) : (
  <App />
);

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  <ErrorBoundary>
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <HistoryRouter history={history} basename={DEMO_BASENAME}>
          {appRoot}
        </HistoryRouter>
      </PersistGate>
    </Provider>
  </ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
